import React, { useState, useRef, useEffect, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { createPortal } from "react-dom";

/** Fullscreen media portals into `document.body`, outside any dialog that is
 * already blocked, so the wrapper opts out of session replay itself. */
const REPLAY_BLOCK_ATTRIBUTES = { "data-sentry-block": "" };

interface UseMediaViewerOptions {
  onError?: (error: string) => void;
}

export const useMediaViewer = (options: UseMediaViewerOptions = {}) => {
  const [rotation, setRotation] = useState<number>(0);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isCustomFullscreen, setIsCustomFullscreen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Update dimensions on resize and initial load
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: rect.width,
          height: rect.height,
        });
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, [isFullscreen, isCustomFullscreen]);

  // Handle escape key to exit custom fullscreen and prevent body scroll
  useEffect(() => {
    if (isCustomFullscreen) {
      const handleEscapeKey = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          setIsCustomFullscreen(false);
        }
      };

      // Prevent body scroll when in custom fullscreen
      const originalBodyStyle = document.body.style.overflow;
      document.body.style.overflow = "hidden";

      // Prevent any modal close behaviors by intercepting all clicks on modal overlays
      const preventModalClose = (e: Event) => {
        const target = e.target as Element;
        if (
          target &&
          (target.closest("[data-radix-dialog-overlay]") ||
            target.classList.contains("modal-overlay") ||
            target.classList.contains("dialog-overlay"))
        ) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
        }
      };

      document.addEventListener("keydown", handleEscapeKey, true);
      document.addEventListener("click", preventModalClose, true);

      return () => {
        document.removeEventListener("keydown", handleEscapeKey, true);
        document.removeEventListener("click", preventModalClose, true);
        document.body.style.overflow = originalBodyStyle;
      };
    }
  }, [isCustomFullscreen]);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const browserFullscreen = !!document.fullscreenElement;
      setIsFullscreen(browserFullscreen);
      // If browser fullscreen ends, also end custom fullscreen
      if (!browserFullscreen && isCustomFullscreen) {
        setIsCustomFullscreen(false);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, [isCustomFullscreen]);

  const handleRotate = useCallback((): void => {
    setRotation((prev) => (prev + 90) % 360);
  }, []);

  const toggleFullscreen = useCallback(async (): Promise<void> => {
    const isCapacitor = Capacitor.isNativePlatform();

    // Check if we're inside a modal/dialog
    const isInModal =
      containerRef.current?.closest('[role="dialog"]') !== null ||
      containerRef.current?.closest(".dialog-content") !== null ||
      containerRef.current?.closest("[data-radix-dialog-content]") !== null;

    if (isCapacitor || isInModal) {
      // Use custom fullscreen for mobile/Capacitor or when inside modal
      setIsCustomFullscreen((prev) => !prev);
      return;
    }

    // Use browser fullscreen for web (when not in modal)
    try {
      if (!document.fullscreenElement && containerRef.current) {
        await containerRef.current.requestFullscreen();
      } else if (document.exitFullscreen) {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error(
        "Error toggling browser fullscreen, falling back to custom fullscreen:",
        err,
      );
      setIsCustomFullscreen((prev) => !prev);
    }
  }, []);

  const handleError = useCallback(
    (errorMessage: string) => {
      setError(errorMessage);
      setIsLoading(false);
      options.onError?.(errorMessage);
    },
    [options],
  );

  const handleLoadStart = useCallback(() => {
    setIsLoading(true);
    setError(null);
  }, []);

  const handleLoadSuccess = useCallback(() => {
    setIsLoading(false);
    setError(null);
  }, []);

  // Calculate optimal media width for mobile
  const getMediaWidth = useCallback((): number => {
    const containerWidth = dimensions.width;
    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      return containerWidth * 0.95; // Use most of the container width on mobile
    }
    return containerWidth * 0.8; // More conservative on desktop
  }, [dimensions.width]);

  // Combine both fullscreen states
  const isAnyFullscreen = isFullscreen || isCustomFullscreen;

  // Helper function to create portal for fullscreen content
  const createFullscreenPortal = useCallback(
    (content: React.ReactNode) => {
      if (isCustomFullscreen && typeof document !== "undefined") {
        // Wrap content to prevent event propagation that could close parent modals
        const wrappedContent = React.createElement(
          "div",
          {
            ...REPLAY_BLOCK_ATTRIBUTES,
            onClick: (e: React.MouseEvent) => e.stopPropagation(),
            onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
            onMouseUp: (e: React.MouseEvent) => e.stopPropagation(),
            onTouchStart: (e: React.TouchEvent) => e.stopPropagation(),
            onTouchEnd: (e: React.TouchEvent) => e.stopPropagation(),
            onTouchMove: (e: React.TouchEvent) => e.stopPropagation(),
            onWheel: (e: React.WheelEvent) => e.stopPropagation(),
            onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
            style: {
              position: "fixed" as const,
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              paddingTop: "var(--ion-safe-area-top, 0px)",
              paddingBottom: "var(--ion-safe-area-bottom, 0px)",
              zIndex: 9999,
              pointerEvents: "auto" as const,
              touchAction: "none" as const,
            },
          },
          content,
        );
        return createPortal(wrappedContent, document.body);
      }
      return content;
    },
    [isCustomFullscreen],
  );

  return {
    // State
    rotation,
    isFullscreen,
    isCustomFullscreen,
    isAnyFullscreen,
    isLoading,
    error,
    dimensions,
    containerRef,

    // Handlers
    handleRotate,
    toggleFullscreen,
    handleError,
    handleLoadStart,
    handleLoadSuccess,
    getMediaWidth,
    createFullscreenPortal,

    // Setters (for specific use cases)
    setError,
    setIsLoading,
  };
};
